/**
 * What build this is.
 *
 * The identity is resolved once, at build time, by `scripts/version.ts` and
 * frozen into the bundle through Vite's `define`. Nothing here reads git, and
 * nothing here runs a request: by the time this module exists the answer is
 * already a literal, so the whole file folds away to a few strings.
 *
 * Two things carry it off the machine that built it: this module, which the
 * help panel prints, and `dist/version.json`, which the same plugin writes so
 * a deploy can be checked with `curl` rather than a browser.
 */

export interface BuildInfo {
  /**
   * The name, in the most precise form the build could manage:
   *
   * - `v2.1.0` — cut exactly on the tag.
   * - `v2.1.0-2-g7eb7178` — two commits past it, from `git describe`.
   * - `v2.1.0+7eb7178` — past it by an unknown distance, because the checkout
   *   had no tags to measure against. See `source`.
   */
  version: string;
  /**
   * The release this build belongs to — `v` and the version in `package.json`,
   * which `bun run release` sets and tags in the same commit. It is always
   * present, because `package.json` is always in the checkout.
   */
  release: string;
  /** The commit, abbreviated to seven — the form every git UI links by. */
  commit: string;
  /** The branch the build was cut from. CI names it; git is asked second. */
  branch: string;
  /** Uncommitted changes were in the tree. Only ever true for a local build. */
  dirty: boolean;
  /** When the build ran, ISO 8601. */
  builtAt: string;
  /**
   * Who named the build.
   *
   * `git` means the checkout carried its own tags, so the distance in
   * `version` is measured. `package` means it did not — CI clones without
   * them — and the release number was read from `package.json` instead. The
   * number is the same either way; only the distance is lost.
   */
  source: 'git' | 'package';
}

/**
 * Replaced verbatim by Vite's `define`. It is declared, never assigned: if the
 * plugin is ever dropped from the config this is a `ReferenceError` on the
 * first import rather than a silently blank version.
 */
declare const __BUILD__: BuildInfo;

export const BUILD: BuildInfo = __BUILD__;

/** Where the releases and commits below can be read. */
export const REPO = 'https://github.com/bangonkali/waning';

/** The commit this build was cut at, on GitHub. */
export function commitUrl(build: BuildInfo = BUILD): string {
  return `${REPO}/commit/${build.commit}`;
}

/** The release this build belongs to, on GitHub. */
export function releaseUrl(build: BuildInfo = BUILD): string {
  return `${REPO}/releases/tag/${build.release}`;
}
